import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAvailableShifts, requestShift, getMyRequests, getComplianceCheck } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Sprout, Calendar, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, User, LogOut, Shield, Hand
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

function getMonday(dateStr) {
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}

function shiftWeek(ws, delta) {
  const d = new Date(ws + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().split('T')[0];
}

function formatWeek(ws) {
  const start = new Date(ws + 'T00:00:00');
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

export default function AvailableShiftsPage() {
  const { user, logout } = useAuth();
  const [weekStart, setWeekStart] = useState(getMonday());
  const [shifts, setShifts] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [shiftsRes, requestsRes, compRes] = await Promise.all([
        getAvailableShifts(weekStart).catch(() => ({ data: { available_shifts: [] } })),
        getMyRequests(weekStart),
        getComplianceCheck(),
      ]);
      setShifts(shiftsRes.data.available_shifts || []);
      setMyRequests(requestsRes.data.requests || []);
      setCompliance(compRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRequest = async (scheduleId, sessionId) => {
    setRequesting(sessionId);
    try {
      await requestShift({ schedule_id: scheduleId, session_id: sessionId });
      toast.success('Shift requested! Waiting for admin approval.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request shift');
    } finally {
      setRequesting(null);
    }
  };

  // Group shifts by day
  const shiftsByDay = {};
  shifts.forEach(s => {
    if (!shiftsByDay[s.day]) shiftsByDay[s.day] = [];
    shiftsByDay[s.day].push(s);
  });

  const isStandby = compliance?.tutor_type === 'standby_tutor';

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center"><Sprout className="w-5 h-5 text-white" /></div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">Available Shifts</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard"><Button variant="outline" size="sm" className="text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Dashboard</Button></Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="text-xs"><User className="w-3 h-3 mr-1" /> {user?.name}</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Week Navigation */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-center px-4">
            <p className="text-sm font-semibold text-[#111827]" data-testid="week-display">{formatWeek(weekStart)}</p>
            <p className="text-[10px] text-[#9CA3AF]">Week of {weekStart}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(getMonday())} className="text-xs text-[#002FA7]">Today</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
        ) : !isStandby ? (
          <Card className="border border-amber-200 bg-amber-50/30" data-testid="not-standby">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-[#111827] mb-2">Standby Tutors Only</h2>
              <p className="text-sm text-[#4B5563]">Available shifts are only visible to standby tutors. Contact your admin if you believe this is an error.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Card className="border border-black/10" data-testid="available-count">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-500">{shifts.length}</p>
                  <p className="text-xs text-[#4B5563]">Available</p>
                </CardContent>
              </Card>
              <Card className="border border-black/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{myRequests.filter(r => r.status === 'pending').length}</p>
                  <p className="text-xs text-[#4B5563]">My Pending</p>
                </CardContent>
              </Card>
              <Card className="border border-black/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#10B981]">{myRequests.filter(r => r.status === 'approved').length}</p>
                  <p className="text-xs text-[#4B5563]">Approved</p>
                </CardContent>
              </Card>
            </div>

            {/* Available Shifts */}
            {shifts.length === 0 ? (
              <Card className="border border-dashed border-gray-300" data-testid="no-shifts">
                <CardContent className="p-10 text-center">
                  <Calendar className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
                  <h2 className="text-lg font-bold text-[#111827] mb-2">No Available Shifts</h2>
                  <p className="text-sm text-[#4B5563]">There are no released shifts available this week. Check back later.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {Object.entries(shiftsByDay).sort(([a], [b]) => {
                  const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
                  return days.indexOf(a) - days.indexOf(b);
                }).map(([day, dayShifts]) => (
                  <Card key={day} className="border border-black/10" data-testid={`day-${day}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-amber-500" />
                        {DAY_LABELS[day]}
                        <span className="text-xs font-normal text-[#9CA3AF]">{dayShifts[0].date}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      {dayShifts.map((shift) => (
                        <div key={shift.session_id} className="flex items-center justify-between p-3 rounded-lg border bg-white border-gray-100" data-testid={`shift-${shift.session_id}`}>
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <p className="text-sm font-bold text-[#111827]">{shift.start_time}</p>
                              <p className="text-[10px] text-[#9CA3AF]">{shift.end_time}</p>
                            </div>
                            <div className="border-l border-gray-200 pl-3">
                              <p className="text-sm font-medium text-[#111827]">{shift.subject || 'Session'}</p>
                              <div className="flex items-center gap-2">
                                {shift.student_name && <span className="text-xs text-[#4B5563]">{shift.student_name}</span>}
                                <span className="text-xs text-[#9CA3AF]">{shift.hours}h</span>
                                <span className="text-xs text-[#9CA3AF]">&middot; was: {shift.original_tutor}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            {shift.my_request_status === 'pending' ? (
                              <Badge className="bg-blue-50 text-blue-700 border-blue-200">Requested</Badge>
                            ) : shift.my_request_status === 'approved' ? (
                              <Badge className="bg-green-50 text-green-700 border-green-200">Approved</Badge>
                            ) : (
                              <Button size="sm" onClick={() => handleRequest(shift.schedule_id, shift.session_id)} disabled={requesting === shift.session_id}
                                className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs" data-testid={`request-${shift.session_id}`}>
                                {requesting === shift.session_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Hand className="w-3 h-3 mr-1" />}
                                Request
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* My Requests History */}
            {myRequests.length > 0 && (
              <Card className="border border-black/10 mt-6" data-testid="my-requests">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">My Requests This Week</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {myRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-2 rounded-lg border border-gray-100 text-sm">
                      <div className="flex items-center gap-2">
                        {req.status === 'approved' ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> :
                         req.status === 'rejected' ? <XCircle className="w-3.5 h-3.5 text-[#EF4444]" /> :
                         <Clock className="w-3.5 h-3.5 text-blue-500" />}
                        <span>{DAY_LABELS[req.day]} {req.start_time}–{req.end_time}</span>
                        <span className="text-[#9CA3AF]">{req.subject}</span>
                      </div>
                      <Badge className={
                        req.status === 'approved' ? 'bg-green-50 text-green-700' :
                        req.status === 'rejected' ? 'bg-red-50 text-red-700' :
                        'bg-blue-50 text-blue-700'
                      }>{req.status}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
